'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Linkedin, MapPin } from 'lucide-react';
import { HubSpotFormModal } from '@/components/hubspot/HubSpotFormModal';

const bookDemoFormId = process.env.NEXT_PUBLIC_HUBSPOT_BOOK_DEMO_FORM_ID || '';

const footerNavigation = {
  solutions: [
    { name: 'Splints', href: '/dental/splints' },
    { name: 'Trays', href: '/dental/trays' },
    { name: 'Models', href: '/dental/models' },
    { name: 'Audio', href: '/audio' },
    { name: 'Smartphone Scanning', href: '/smartphone-scanning' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'News', href: '/news' },
    { name: 'Careers', href: '/careers' },
  ],
  legal: [{ name: 'Privacy Policy', href: '/privacy' }],
};

const offices = [
  { name: 'Australia', city: 'Melbourne' },
  { name: 'Denmark', city: 'Copenhagen' },
  { name: 'France', city: 'Aix-en-Provence' },
  { name: 'Ireland', city: 'Dublin' },
];

const socialMedia = [
  { name: 'LinkedIn', icon: Linkedin, href: 'https://au.linkedin.com/company/hearables-3d' },
  // { name: 'Twitter', icon: Twitter, href: '#' },
  // { name: 'Facebook', icon: Facebook, href: '#' },
];

export function Footer() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <footer className="bg-gray-900" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto max-w-[90rem] px-6 pb-8 pt-12 sm:pt-16 lg:px-8 lg:pt-20">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="space-y-8">
            <div className="flex items-start gap-10">
              <Image
                src="/logo-white.svg"
                alt="H3D Logo"
                className="h-18 w-auto"
                width={72}
                height={72}
                loading="lazy"
              />
              <p className="text-xs leading-tight text-gray-300">
                Reinventing the
                <br />
                custom-fit journey
                <br />
                with AI and automation
              </p>
            </div>
            <div className="flex space-x-6">
              {socialMedia.map((item) => (
                <a key={item.name} href={item.href} className="text-gray-400 hover:text-gray-300">
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-8 md:grid-cols-3 xl:col-span-2 xl:mt-0">
            <div>
              <h3 className="text-sm font-semibold leading-6 text-white">Solutions</h3>
              <ul role="list" className="mt-6 space-y-4">
                {footerNavigation.solutions.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm leading-6 text-gray-300 hover:text-white"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold leading-6 text-white">Company</h3>
              <ul role="list" className="mt-6 space-y-4">
                {footerNavigation.company.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm leading-6 text-gray-300 hover:text-white"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="text-sm leading-6 text-gray-300 hover:text-white"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold leading-6 text-white">Legal</h3>
              <ul role="list" className="mt-6 space-y-4">
                {footerNavigation.legal.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-sm leading-6 text-gray-300 hover:text-white"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Office Locations */}
        <div className="mt-12 border-t border-gray-800 pt-8">
          <h3 className="text-sm font-semibold leading-6 text-white mb-4 flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Global Offices
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {offices.map((office) => (
              <div key={office.name} className="text-sm text-gray-300">
                <div className="font-medium">{office.name}</div>
                <div className="text-gray-400">{office.city}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-12 border-t border-gray-800 pt-8 sm:mt-16 lg:mt-20">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <p className="text-xs leading-5 text-gray-400">
              &copy; {new Date().getFullYear()} H3D. All rights reserved.
            </p>
            <p className="text-xs leading-5 text-gray-400 text-center">
              L11, 655 Elizabeth Street. Melbourne 3000, Australia
              <br />
              ABN 69 625 253 661
            </p>
            <div className="flex gap-6 mt-4 sm:mt-0">
              {footerNavigation.legal.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="text-xs leading-5 text-gray-400 hover:text-gray-300"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <HubSpotFormModal
        formId={bookDemoFormId}
        open={contactOpen}
        onOpenChange={setContactOpen}
        title="Contact us"
        description="Fill out the form below and our team will contact you as soon as we can."
      />
    </footer>
  );
}
